import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { clearAdminSession } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Logo from "../components/Logo";

export default function AdminLogin() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const token = params.get("token");
    const expiresIn = Number(params.get("expires_in"));
    if (token && Number.isFinite(expiresIn) && expiresIn > 0) {
      clearAdminSession();
      sessionStorage.setItem("xs_admin_token", token);
      sessionStorage.setItem("xs_admin_exp", String(Date.now() + expiresIn * 1000));
      window.history.replaceState({}, "", window.location.pathname);
      window.dispatchEvent(new Event("xs-auth-changed"));
      nav("/admin", { replace: true });
      return;
    }
    api.get("/admin/google/enabled")
      .then(({ data }) => setGoogleEnabled(Boolean(data.enabled)))
      .catch(() => setGoogleEnabled(false));
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { username, password });
      clearAdminSession();
      sessionStorage.setItem("xs_admin_token", data.token);
      const exp = Date.now() + (data.expires_in * 1000);
      sessionStorage.setItem("xs_admin_exp", String(exp));
      window.dispatchEvent(new Event("xs-auth-changed"));
      nav("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/google/start");
      window.location.assign(data.authorization_url);
    } catch (err) {
      setError(err.response?.data?.detail || "Google sign-in failed");
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <main className="xs-page" data-testid="admin-login-page">
        <div className="xs-container" style={{ maxWidth: 440 }}>
          <div style={{ textAlign: "center", margin: "20px 0 30px" }}>
            <Logo className="xs-hero-logo" />
            <h1 style={{ margin: "10px 0 4px", color: "#ffde16" }}>admin</h1>
            <div style={{ opacity: 0.7, fontSize: "0.9em" }}>single-user access</div>
          </div>
          <form onSubmit={submit} data-testid="admin-login-form">
            <label className="xs-label">username</label>
            <input
              className="xs-input"
              type="email"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Email"
              data-testid="admin-username-input"
              autoComplete="username"
            />
            <label className="xs-label">password</label>
            <input className="xs-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" data-testid="admin-password-input" autoComplete="current-password" />
            {error && (
              <div
                style={{ color: "#ff6b6b", fontSize: "0.9em", margin: "10px 0" }}
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}
            <div style={{ marginTop: 18 }}>
              <button
                type="submit"
                className="xs-btn primary"
                disabled={loading || !username || !password}
                data-testid="admin-login-submit"
                style={{ width: "100%" }}
              >
                {loading ? "authenticating…" : "sign in"}
              </button>
            </div>
            {googleEnabled && (
              <button
                type="button"
                className="xs-btn ghost"
                onClick={signInWithGoogle}
                disabled={loading}
                style={{ width: "100%", marginTop: 10 }}
              >
                sign in with Google
              </button>
            )}
            <div style={{ opacity: 0.5, fontSize: "0.75em", marginTop: 16, lineHeight: 1.5 }}>
              protected by rate-limit (8 attempts / 15 min / ip),
              JWT sessions expire in 1 hour, all actions audited.
            </div>
          </form>
        </div>
      </main>
      <Footer show />
    </div>
  );
}

export function useRequireAdmin() {
  const nav = useNavigate();
  React.useEffect(() => {
    const token = sessionStorage.getItem("xs_admin_token");
    const exp = Number(sessionStorage.getItem("xs_admin_exp") || 0);
    if (!token || Date.now() > exp) {
      clearAdminSession();
      nav("/admin/login");
      return;
    }
    let active = true;
    api.get("/admin/verify").catch((error) => {
      if (!active || ![401, 403].includes(error.response?.status)) return;
      clearAdminSession();
      nav("/admin/login", { replace: true });
    });
    return () => { active = false; };
  }, [nav]);
}
